/**
 * 狂一鍋 interval 訂位頁的 UI 字串字典。
 *
 * 字串由 prototype/assets/i18n.js 移植而來（只取訂位流程會用到的鍵），
 * 讓 Vue 版與原型頁的文案完全一致，避免同一句話兩邊各寫各的。
 *
 * ⚠️ 語言來源與原型不同：
 *   原型讀 localStorage 的 `fr_lang`（靜態站沒有後端語言狀態可用）；
 *   本檔改吃 Windwalker LocaleMiddleware 決定、由 blade 傳進來的 locale
 *   （'zh-TW' / 'en-US'），才不會出現「網站語言是英文、訂位表單卻是中文」。
 *
 * 動態資料（店名、地址）一律用 API 回傳的 tw / en 欄位，不放這裡。
 */
const zh = {
    'common.prev': '上一步',
    'bk.title': '線上訂位',
    'bk.subtitle': '狂一鍋 ・ 新台式火鍋',
    'bk.free': '本店免訂金',
    'bk.partyTitle': '用餐人數',
    'bk.allDay': '整天',
    'bk.noon': '中午',
    'bk.evening': '晚上',
    'bk.capacityNote': '若有超過門市單次可訂位人數上限的訂位需求，敬請聯繫門市協助訂位。',
    'bk.storeMaxParty': '單次可訂位{n}人',
    'bk.date': '用餐日期',
    'bk.store': '門店',
    'bk.storeAll': '全部門市',
    'bk.storeNoResult': '查無此門市',
    'bk.adult': '大人',
    'bk.child': '兒童',
    'bk.childSeat': '兒童椅',
    'bk.childSeatHint': '兒童椅數量不可超過兒童人數',
    'bk.s3title': '填寫訂位資料',
    'bk.name': '姓名',
    'bk.gender': '性別',
    'bk.gender.other': '其他/不透漏',
    'bk.gender.male': '男性',
    'bk.gender.female': '女性',
    'bk.phone': '手機號碼',
    'bk.email': 'Email',
    'bk.note': '備註（選填）',
    'bk.notePlaceholder': '兒童椅、慶生、過敏…',
    'bk.namePlaceholder': '請輸入姓名',
    'bk.emailPlaceholder': '請輸入Email',
    'bk.emailOptional': '（選填；未填將收不到訂位確認信）',
    'bk.sendOtp': '發送驗證碼',
    'bk.startBooking': '開始預訂',
    // 上限值與後端 ReservationEligibilityService::ACTIVE_BOOKING_LIMITS[3] 對齊（狂一鍋 2 筆）；
    // 後端調整上限時這句要跟著改，API 只回布林值、不會告訴前端上限是多少。
    'bk.dupBooking': '您目前已有 2 筆訂位，每位客戶同時最多保留 2 筆訂位。',
    'bk.purpose': '用餐目的（可複選、選填）',
    'bk.purpose.friend-birthday': '朋友慶生',
    'bk.purpose.friend-gathering': '朋友聚餐',
    'bk.purpose.birthday': '慶生',
    'bk.purpose.date': '約會',
    'bk.purpose.anniversary': '週年慶',
    'bk.purpose.special-day': '紀念日',
    'bk.purpose.family': '家庭用餐',
    'bk.purpose.business': '商務用餐',
    'bk.s4title': '手機驗證',
    'bk.otpHint': '驗證碼已發送至',
    'bk.otpDemo': '請輸入收到的 6 位數驗證碼',
    'bk.confirm': '確認訂位',
    'bk.doneTitle': '訂位成功！',
    'bk.redirecting': '訂位成功！正在為您開啟訂位明細…',
    'bk.doneNotify': '訂位確認將以簡訊／Email 通知您',
    'bk.doneManageHint': '需要查詢或取消訂位嗎？',
    'bk.doneManage': '查詢／退訂',
    'bk.sumStore': '門市',
    'bk.sumDate': '日期',
    'bk.sumSlot': '時段',
    'bk.sumParty': '人數',
    'bk.sumDeposit': '訂金',
    'bk.sumOrderNo': '訂位編號',
    'bk.free2': '免訂金',
    'bk.slotLoading': '載入可訂時段…',
    'bk.slotEmpty': '當日無可訂時段',
    'bk.slotFail': '載入失敗，請稍後再試',
    'bk.slotFull': '已滿',
    'bk.sending': '送出中…',
    'bk.partyAdult': '大人',
    'bk.partyChild': '兒童',
    'bk.none': '-',
    'bk.alertNoSlot': '請先選擇時段',
    'bk.errRequired': '請填寫這個欄位。',
    'bk.errName': '姓名不可包含特殊符號（僅限中英文、空白、.、-）',
    'bk.errPhone': '手機號碼格式有誤',
    'bk.errEmail': '該欄位須為有效的電子信箱。',
    'bk.errEmailStrict': 'Email 不可包含逗號、空白或其他非法字元。',
    // 隱私權同意（與明水然系同一組文案，見各站 reservation.ini 的 checkbox.policy）
    'bk.policyAgree': '按下確認訂位代表我已閱讀並同意',
    'bk.policyName': '樂多多隱私權條款',
    'bk.errPolicy': '請先閱讀並同意樂多多隱私權條款。',
    'bk.noticeInstruction': '訂位須知',
    'bk.noticeReminder': '溫馨提醒',
    'common.close': '關閉',
    'common.gotIt': '我知道了',
    'bk.otpBlocked': '目前無法訂位，請稍後再試或聯繫店家',
    'bk.otpCaptcha': '系統需要圖形驗證，請稍後再試或聯繫店家',
    'bk.otpSendFail': '驗證碼發送失敗，請稍後再試',
    'bk.otpVerifyFail': '驗證碼錯誤，請重新輸入',
    'bk.otpIncomplete': '請輸入完整 6 位數驗證碼',
    'bk.otpResend': '重新發送驗證碼',
    'bk.otpResendIn': '{n} 秒後可重新發送',
    'bk.otpExpiresIn': '驗證碼 {n} 後失效',
    'bk.otpExpired': '驗證碼已失效，請重新發送',
    'bk.otpResent': '驗證碼已重新發送，請查收',
    'bk.otpResendTooFrequent': '重新發送太過頻繁，請稍候再試',
    // 重發被後端擋下時附在錯誤訊息後面：這一頁再按幾次都不會成功，
    // 退回上一步重新送出才會走一次完整的驗證流程。
    'bk.otpResendFallbackHint': '請按「上一步」後重新送出訂位資料。',
    'bk.eligFail': '無法確認訂位資格，請稍後再試',
    'bk.alertFailPrefix': '訂位失敗：',
    'bk.alertFail': '訂位失敗，請稍後再試',
    'bk.alertUncertain': '網路連線不穩，目前無法確認訂位是否已成立。請先確認是否收到訂位成功的簡訊或 Email，再決定是否重新訂位，以免重複訂位。',
    'bk.networkFail': '網路連線不穩或逾時，這次操作沒有完成，請確認網路後再試一次。',
    'bk.notFilled': '(未填)',
    'bk.nearest': '離您最近',
    'bk.pagerCount': '第 {from}–{to} 家／共 {total} 家門市',
    'bk.pagerPrev': '上一頁',
    'bk.pagerNext': '下一頁',
    'bk.countryNoResult': '查無此國家',
    'st.loading': '門市載入中…',
    'st.empty': '目前無門市資料',
    'st.fail': '門市載入失敗，請稍後再試',
    'rg.all': '全部',
    'rg.north': '北部',
    'rg.central': '中部',
    'rg.south': '南部',
    'rg.east': '東部',
};
const en = {
    'common.prev': 'Back',
    'bk.title': 'Online Reservation',
    'bk.subtitle': 'Fondue Retro ・ New Taiwanese Hot Pot',
    'bk.free': 'No Deposit',
    'bk.partyTitle': 'Party Size',
    'bk.allDay': 'All Day',
    'bk.noon': 'Noon',
    'bk.evening': 'Evening',
    'bk.capacityNote': 'For parties larger than a store\'s per-booking limit, please contact the store for assistance.',
    'bk.storeMaxParty': 'Up to {n} guests per booking',
    'bk.date': 'Date',
    'bk.store': 'Restaurant',
    'bk.storeAll': 'All restaurants',
    'bk.storeNoResult': 'No matching restaurant',
    'bk.adult': 'Adults',
    'bk.child': 'Children',
    'bk.childSeat': 'High Chairs',
    'bk.childSeatHint': 'High chairs cannot exceed the number of children',
    'bk.s3title': 'Your Details',
    'bk.name': 'Name',
    'bk.gender': 'Gender',
    'bk.gender.other': 'Other / Prefer not to say',
    'bk.gender.male': 'Male',
    'bk.gender.female': 'Female',
    'bk.phone': 'Mobile',
    'bk.email': 'Email',
    'bk.note': 'Note (optional)',
    'bk.notePlaceholder': 'High chair, birthday, allergies…',
    'bk.namePlaceholder': 'Enter your name',
    'bk.emailPlaceholder': 'Enter your email',
    'bk.emailOptional': ' (optional; no confirmation email without it)',
    'bk.sendOtp': 'Send Code',
    'bk.startBooking': 'Start Booking',
    'bk.dupBooking': 'You already have 2 reservations. Each customer may hold up to 2 at a time.',
    'bk.purpose': 'Meal purpose (optional, multiple choice)',
    'bk.purpose.friend-birthday': "Friend's Birthday",
    'bk.purpose.friend-gathering': 'Friends Gathering',
    'bk.purpose.birthday': 'Birthday',
    'bk.purpose.date': 'Dating',
    'bk.purpose.anniversary': 'Anniversary',
    'bk.purpose.special-day': 'Special Day',
    'bk.purpose.family': 'Family Meal',
    'bk.purpose.business': 'Business Dining',
    'bk.s4title': 'Verification',
    'bk.otpHint': 'Code sent to',
    'bk.otpDemo': 'Enter the 6-digit code you received',
    'bk.confirm': 'Confirm',
    'bk.doneTitle': 'Reserved!',
    'bk.redirecting': 'Reserved! Opening your reservation details…',
    'bk.doneNotify': 'Confirmation will be sent via SMS / Email',
    'bk.doneManageHint': 'Need to view or cancel your reservation?',
    'bk.doneManage': 'View / Cancel',
    'bk.sumStore': 'Location',
    'bk.sumDate': 'Date',
    'bk.sumSlot': 'Time',
    'bk.sumParty': 'Party',
    'bk.sumDeposit': 'Deposit',
    'bk.sumOrderNo': 'Order No.',
    'bk.free2': 'No deposit',
    'bk.slotLoading': 'Loading time slots…',
    'bk.slotEmpty': 'No available time slots',
    'bk.slotFail': 'Failed to load, please try again',
    'bk.slotFull': 'Full',
    'bk.sending': 'Submitting…',
    'bk.partyAdult': 'Adults',
    'bk.partyChild': 'Children',
    'bk.none': '-',
    'bk.alertNoSlot': 'Please select a time slot first',
    'bk.errRequired': 'This field is required.',
    'bk.errName': 'Name contains invalid characters.',
    'bk.errPhone': 'Invalid phone number',
    'bk.errEmail': 'This field must be a valid email address.',
    'bk.errEmailStrict': 'Email cannot contain invalid characters (comma, space, etc.).',
    'bk.policyAgree': 'Clicking to confirm the reservation means that I have read and agreed to it.',
    'bk.policyName': 'Ledodo Privacy Policy',
    'bk.errPolicy': 'Please read and agree to the Ledodo Privacy Policy first.',
    'bk.noticeInstruction': 'Reservation Notice',
    'bk.noticeReminder': 'Friendly Reminder',
    'common.close': 'Close',
    'common.gotIt': 'Got it',
    'bk.otpBlocked': 'Unable to book right now. Please try later or contact the store.',
    'bk.otpCaptcha': 'Image verification is required. Please try later or contact the store.',
    'bk.otpSendFail': 'Failed to send the code. Please try again.',
    'bk.otpVerifyFail': 'Incorrect code. Please try again.',
    'bk.otpIncomplete': 'Please enter the full 6-digit code',
    'bk.otpResend': 'Resend code',
    'bk.otpResendIn': 'Resend available in {n}s',
    'bk.otpExpiresIn': 'Code expires in {n}',
    'bk.otpExpired': 'The code has expired. Please resend it.',
    'bk.otpResent': 'A new code has been sent.',
    'bk.otpResendTooFrequent': 'Too many requests. Please wait a moment.',
    'bk.otpResendFallbackHint': 'Please go back and submit your booking details again.',
    'bk.eligFail': 'Unable to verify booking eligibility. Please try again.',
    'bk.alertFailPrefix': 'Reservation failed: ',
    'bk.alertFail': 'Reservation failed, please try again',
    'bk.alertUncertain': 'We could not confirm whether your reservation was completed due to a network problem. Please check whether you received a confirmation SMS or email before booking again to avoid a duplicate reservation.',
    'bk.networkFail': 'The network is unstable or timed out, so this step was not completed. Please check your connection and try again.',
    'bk.notFilled': '(empty)',
    'bk.nearest': 'Nearest to you',
    'bk.pagerCount': '{from}–{to} of {total} stores',
    'bk.pagerPrev': 'Previous page',
    'bk.pagerNext': 'Next page',
    'bk.countryNoResult': 'No matching country',
    'st.loading': 'Loading…',
    'st.empty': 'No locations available',
    'st.fail': 'Failed to load, please try again',
    'rg.all': 'All',
    'rg.north': 'North',
    'rg.central': 'Central',
    'rg.south': 'South',
    'rg.east': 'East',
};
const DICTS = { zh, en };
/**
 * 目前語系代碼（'zh' | 'en'）。
 *
 * 由 blade 傳進來的 Windwalker locale 決定；預設中文。
 * 用模組層變數而不是 reactive：訂位頁的語言在載入時就固定，
 * 頁內沒有即時切換語言的入口（切語言走 Windwalker 換頁）。
 */
let currentLang = 'zh';
export function setIntervalLang(locale) {
    currentLang = String(locale || '').toLowerCase().startsWith('en') ? 'en' : 'zh';
}
export function intervalLang() {
    return currentLang;
}
/** 是否英文介面。摘要分隔符、地區標籤等要用。 */
export function isEn() {
    return currentLang === 'en';
}
/**
 * 取字串。找不到的鍵回退中文，再找不到就回鍵名本身
 * （寧可畫面上出現 key 讓人看得出漏翻，也不要顯示空白）。
 */
export function t(key, replaces) {
    const dict = DICTS[currentLang] || zh;
    let str = key in dict ? dict[key] : (zh[key] ?? key);
    if (replaces) {
        for (const [k, v] of Object.entries(replaces)) {
            str = str.replace(`{${k}}`, String(v));
        }
    }
    return str;
}
/** 標籤分隔符：中文全形「：」；英文半形「:」前後留空白。 */
export function sep() {
    return isEn() ? ' : ' : '：';
}
/** 送 API 的語言代碼（後端 OTP／通知模板用 zh-TW / en-US）。 */
export function apiLang() {
    return isEn() ? 'en-US' : 'zh-TW';
}
